import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Lock, Bell, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [prefs, setPrefs] = useState(null);

  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  useEffect(() => { if (settings && !prefs) setPrefs(settings); }, [settings, prefs]);

  const [inv, setInv] = useState(null);
  const [savingInv, setSavingInv] = useState(false);
  const { data: invSettings } = useQuery({ queryKey: ["invoice-settings"], queryFn: async () => (await api.get("/admin/invoice-settings")).data });
  useEffect(() => { if (invSettings && !inv) setInv(invSettings); }, [invSettings, inv]);

  const saveInvoiceSettings = async () => {
    setSavingInv(true);
    try {
      await api.put("/admin/invoice-settings", {
        company_number: inv.company_number || "",
        vat_number: inv.vat_number || "",
        default_vat_enabled: !!inv.default_vat_enabled,
        default_vat_rate: Number(inv.default_vat_rate) || 20,
        payment_terms_days: Number(inv.payment_terms_days) || 14,
        terms: inv.terms || "",
        bank_details: inv.bank_details || "",
      });
      qc.invalidateQueries({ queryKey: ["invoice-settings"] });
      toast.success("Invoice settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingInv(false); }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("Passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/admin/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    await api.patch("/admin/settings", { [key]: next[key] });
    qc.invalidateQueries({ queryKey: ["settings"] });
    toast.success("Preferences updated");
  };

  const saveAlertEmail = async () => {
    try {
      await api.patch("/admin/settings", { notification_email: prefs.notification_email });
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Alert email saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendTest = async () => {
    try {
      await api.post("/admin/settings/test-email");
      toast.success(`Test email sent to ${prefs.notification_email}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const inputCls = "w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100";

  return (
    <div data-testid="settings-page">
      <h1 className="font-display font-black text-slate-900 text-2xl sm:text-3xl">Settings</h1>
      <p className="text-slate-500 mt-1">Signed in as {user?.email}</p>

      <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center gap-2 mb-4"><Lock className="h-5 w-5 text-green-600" /><h2 className="font-display font-bold text-slate-900">Change password</h2></div>
        <div className="space-y-4 max-w-md">
          <input type="password" className={inputCls} placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="settings-current-pw" />
          <input type="password" className={inputCls} placeholder="New password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="settings-new-pw" />
          <input type="password" className={inputCls} placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="settings-confirm-pw" />
          <button onClick={changePassword} disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 font-bold text-white hover:bg-green-700 disabled:opacity-60" data-testid="settings-change-pw">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
          </button>
        </div>
      </div>

      {prefs && (
        <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4"><Bell className="h-5 w-5 text-green-600" /><h2 className="font-display font-bold text-slate-900">Notification preferences</h2></div>
          <div className="space-y-3">
            {[
              { key: "email_notifications", label: "Email me when a new lead arrives" },
            ].map((p) => (
              <label key={p.key} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 cursor-pointer" data-testid={`pref-${p.key}`}>
                <span className="text-sm font-medium text-slate-700">{p.label}</span>
                <button type="button" onClick={() => togglePref(p.key)} className={`relative h-6 w-11 rounded-full transition ${prefs[p.key] ? "bg-green-600" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition ${prefs[p.key] ? "translate-x-5" : ""}`} />
                </button>
              </label>
            ))}
            <div className="max-w-xl">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Send lead alerts to</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input type="email" className={inputCls} placeholder="you@example.com" value={prefs.notification_email || ""} onChange={(e) => setPrefs({ ...prefs, notification_email: e.target.value })} data-testid="settings-alert-email" />
                <button onClick={saveAlertEmail} className="rounded-xl bg-green-600 px-5 py-2.5 font-bold text-white hover:bg-green-700 whitespace-nowrap" data-testid="settings-alert-email-save">Save</button>
                <button onClick={sendTest} disabled={!settings?.notification_email} className="rounded-xl border border-slate-300 px-5 py-2.5 font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50 whitespace-nowrap" data-testid="settings-alert-email-test">Send test</button>
              </div>
              <p className="text-xs text-slate-400 mt-2">Password reset emails also go to this address.</p>
            </div>
          </div>
        </div>
      )}

      {inv && (
        <div className="mt-4 rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4"><Receipt className="h-5 w-5 text-green-600" /><h2 className="font-display font-bold text-slate-900">Invoice settings</h2></div>
          <div className="space-y-4 max-w-xl">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Company number</label><input className={inputCls} value={inv.company_number || ""} onChange={(e) => setInv({ ...inv, company_number: e.target.value })} data-testid="inv-company-number" /></div>
              <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">VAT number (optional)</label><input className={inputCls} value={inv.vat_number || ""} onChange={(e) => setInv({ ...inv, vat_number: e.target.value })} data-testid="inv-vat-number" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 items-end">
              <label className="flex items-center gap-3"><input type="checkbox" checked={!!inv.default_vat_enabled} onChange={(e) => setInv({ ...inv, default_vat_enabled: e.target.checked })} className="h-4 w-4 accent-green-600" data-testid="inv-default-vat" /><span className="text-sm font-semibold text-slate-700">Charge VAT by default</span></label>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">VAT rate %</label><input type="number" className={inputCls} value={inv.default_vat_rate ?? 20} onChange={(e) => setInv({ ...inv, default_vat_rate: e.target.value })} data-testid="inv-vat-rate" /></div>
                <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Terms (days)</label><input type="number" className={inputCls} value={inv.payment_terms_days ?? 14} onChange={(e) => setInv({ ...inv, payment_terms_days: e.target.value })} data-testid="inv-terms-days" /></div>
              </div>
            </div>
            <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Bank / payment details (shown on invoice)</label><textarea rows={2} className={inputCls} value={inv.bank_details || ""} onChange={(e) => setInv({ ...inv, bank_details: e.target.value })} data-testid="inv-bank-details" /></div>
            <div><label className="block text-sm font-semibold text-slate-700 mb-1.5">Invoice terms / footer note</label><textarea rows={2} className={inputCls} value={inv.terms || ""} onChange={(e) => setInv({ ...inv, terms: e.target.value })} data-testid="inv-terms" /></div>
            <button onClick={saveInvoiceSettings} disabled={savingInv} className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 font-bold text-white hover:bg-green-700 disabled:opacity-60" data-testid="inv-settings-save">
              {savingInv ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save invoice settings"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
